import { useGetPosStatusQuery } from '@/store/accountApi';
import { formatDateTime } from '@/utils/format';

const PROVIDER_LABELS: Record<string, string> = {
  CLOVER: 'Clover',
  SKYTAB: 'SkyTab / Shift4',
  GIVEX: 'Givex',
  NONE: 'No POS'
};

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  CONNECTED: { label: 'Connected', tone: 'bg-jade-soft text-jade' },
  SYNCING: { label: 'Syncing', tone: 'bg-amber-soft text-amber' },
  DISCONNECTED: { label: 'Disconnected', tone: 'bg-paper text-slate' },
  EXPIRED: { label: 'Credentials expired', tone: 'bg-rust-soft text-rust' },
  ERROR: { label: 'Error', tone: 'bg-rust-soft text-rust' },
  // Not a fault: we have never been able to attempt a connection.
  AWAITING_PARTNER_ACCESS: { label: 'Awaiting partner access', tone: 'bg-amber-soft text-amber' }
};

/**
 * A compact statement of what the till is doing, for the top of the Menu and
 * Orders pages.
 *
 * "Awaiting partner access" is deliberately distinct from "error": one is a
 * commercial dependency on our side, the other is something the restaurant can
 * act on. Collapsing them would send an owner hunting for a fault that is ours.
 */
export function PosStatusBanner({ locationId }: { locationId?: string }) {
  const { data } = useGetPosStatusQuery();

  const entry = locationId
    ? data?.data.find((row) => row.locationId === locationId)
    : data?.data[0];

  if (!entry || entry.provider === 'NONE') return null;

  const status = STATUS_LABELS[entry.status] ?? STATUS_LABELS.DISCONNECTED;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded border border-rule bg-card px-4 py-2.5">
      <span className="text-small text-ink">
        Connected POS: {PROVIDER_LABELS[entry.provider] ?? entry.provider}
      </span>

      <span className={`rounded px-1.5 py-0.5 text-micro ${status?.tone}`}>{status?.label}</span>

      {entry.merchantId ? (
        <span className="tabular text-micro text-slate">Merchant {entry.merchantId}</span>
      ) : null}

      {entry.lastSyncAt ? (
        <span className="text-micro text-slate">
          Menu synced {formatDateTime(entry.lastSyncAt)}
          {entry.lastSyncItemCount ? ` · ${entry.lastSyncItemCount} items` : ''}
        </span>
      ) : (
        <span className="text-micro text-slate">Menu never synced</span>
      )}

      {entry.lastOrderSentAt ? (
        <span className="text-micro text-slate">
          Last order sent {formatDateTime(entry.lastOrderSentAt)}
          {entry.lastOrderNumber ? ` · #${entry.lastOrderNumber}` : ''}
        </span>
      ) : null}

      {entry.ordersNeedingAttention > 0 ? (
        <span className="rounded bg-rust-soft px-1.5 py-0.5 text-micro text-rust">
          {entry.ordersNeedingAttention} order
          {entry.ordersNeedingAttention === 1 ? '' : 's'} need checking
        </span>
      ) : null}

      {entry.lastSyncError ? (
        <span className="w-full text-micro text-rust">Last sync error: {entry.lastSyncError}</span>
      ) : null}

      {entry.status === 'AWAITING_PARTNER_ACCESS' ? (
        <span className="w-full text-micro text-slate">
          Orders are taken and recorded here, but cannot be sent to this POS until certified
          partner credentials are issued. Payment links are unaffected.
        </span>
      ) : null}

      {!entry.capabilities?.supportsInventorySync && entry.status === 'CONNECTED' ? (
        <span className="w-full text-micro text-slate">
          This POS does not report sold-out status, so availability is managed here.
        </span>
      ) : null}
    </div>
  );
}
